export const SANDBOX_FILE_PREFIX = "sandbox:/mnt/data/"

export function tryDecodeUriComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

// Some LLMs may emit sandbox-prefixed paths like "sandbox:/mnt/data/<filename>"
export function stripSandboxPrefix(value: string): string {
  return typeof value === "string" && value.startsWith(SANDBOX_FILE_PREFIX)
    ? value.slice(SANDBOX_FILE_PREFIX.length)
    : value
}

export function getBasenameFromPath(value: string): string {
  if (!value) return ""
  let v = stripSandboxPrefix(value)
  // Remove query/hash if any
  v = v.split(/[?#]/)[0]
  const parts = v.split("/")
  return parts.length ? parts[parts.length - 1] : v
}

/** Decode an inline image src and drop the sandbox prefix, as the inline renderer reads it. */
export function normalizeInlineImageSrc(src: string | undefined): string {
  return stripSandboxPrefix(tryDecodeUriComponent(String(src || "")))
}

/**
 * The session file an inline image src names, or null when the src points
 * anywhere else (an external URL, another session's file, a subfolder).
 *
 * Accepts the forms models write for a top-level file of this chat:
 * `sandbox:` with or without `/mnt/data/` in any letter case, `attachment:`,
 * `/mnt/data/`, `./`, a bare filename, and this session's
 * `/api/session-content/<session>/` path.
 */
function sessionFileNamedBy(src: string | undefined, sessionId: string | undefined): string | null {
  const value = tryDecodeUriComponent(String(src || "")).trim()
  const path = value.split(/[?#]/)[0]
  const lower = path.toLowerCase()
  const sessionContentPrefix = sessionId ? `/api/session-content/${sessionId}/` : null

  let name: string
  if (lower.startsWith("sandbox:")) {
    name = path.slice("sandbox:".length).replace(/^\/mnt\/data\//i, "")
  } else if (lower.startsWith("attachment:")) {
    name = path.slice("attachment:".length)
  } else if (lower.startsWith("/mnt/data/")) {
    name = path.slice("/mnt/data/".length)
  } else if (path.startsWith("./")) {
    name = path.slice("./".length)
  } else if (sessionContentPrefix && path.startsWith(sessionContentPrefix)) {
    name = path.slice(sessionContentPrefix.length)
  } else if (!path.includes(":")) {
    name = path
  } else {
    return null
  }
  // A single top-level filename only: a subfolder names a different file.
  return name.length > 0 && !name.includes("/") ? name : null
}

/**
 * True when an inline markdown image points at a file this message already shows
 * as a generated-image card, so the answer would show it twice (#3473).
 *
 * Matches the exact filename only: a substring match would also hide an
 * unrelated image whose name merely contains the card's filename.
 */
export function isCardImageSrc(
  src: string | undefined,
  cardFiles: ReadonlySet<string>,
  sessionId?: string
): boolean {
  if (cardFiles.size === 0) return false
  const name = sessionFileNamedBy(src, sessionId)
  return name !== null && cardFiles.has(name)
}
